import { getGoogleMapsBrowserApiKey } from '@/lib/google-maps-api-key'
import { importLibrary, setOptions } from '@googlemaps/js-api-loader'

const GOOGLE_MAPS_LOADER_STATE_KEY = '__tukiosGoogleMapsLoaderState__'
const GOOGLE_MAPS_VERSION = 'weekly'

/** The Maps JavaScript API libraries admin loads. */
interface GoogleMapsLibraries {
  geocoding: google.maps.GeocodingLibrary
  geometry: google.maps.GeometryLibrary
  maps: google.maps.MapsLibrary
  marker: google.maps.MarkerLibrary
  places: google.maps.PlacesLibrary
  routes: google.maps.RoutesLibrary
}

export type GoogleMapsLibraryName = keyof GoogleMapsLibraries

type GoogleMapsLibraryPromises<TLibraryName extends GoogleMapsLibraryName> = {
  [TName in TLibraryName]?: Promise<GoogleMapsLibraries[TName]>
}

interface GoogleMapsLoaderState {
  configured: boolean
  libraryPromises: GoogleMapsLibraryPromises<GoogleMapsLibraryName>
}

declare global {
  // The loader keeps its state on globalThis so every module instance shares one Maps API load.
  var __tukiosGoogleMapsLoaderState__: GoogleMapsLoaderState | undefined
}

function getLoaderState(): GoogleMapsLoaderState {
  if (!globalThis[GOOGLE_MAPS_LOADER_STATE_KEY]) {
    globalThis[GOOGLE_MAPS_LOADER_STATE_KEY] = {
      configured: false,
      libraryPromises: {},
    }
  }

  return globalThis[GOOGLE_MAPS_LOADER_STATE_KEY]
}

function getGoogleMapsApiKey(): string {
  const apiKey = getGoogleMapsBrowserApiKey()

  if (!apiKey) {
    throw new Error('Google Maps API key is required')
  }

  return apiKey
}

export function configureGoogleMapsLoader(): void {
  const state = getLoaderState()

  if (state.configured) {
    return
  }

  setOptions({
    key: getGoogleMapsApiKey(),
    v: GOOGLE_MAPS_VERSION,
  })
  state.configured = true
}

export function importGoogleMapsLibrary<TLibraryName extends GoogleMapsLibraryName>(
  libraryName: TLibraryName
): Promise<GoogleMapsLibraries[TLibraryName]> {
  configureGoogleMapsLoader()

  const libraryPromises: GoogleMapsLibraryPromises<TLibraryName> = getLoaderState().libraryPromises

  return (libraryPromises[libraryName] ??= importLibrary(libraryName))
}

export function resetGoogleMapsLoaderForTests(): void {
  delete globalThis[GOOGLE_MAPS_LOADER_STATE_KEY]
}
